/**
 * Pure helpers for normalizing KC growth-phase duration keys.
 *
 * Crop records and some older field payloads use `duration_days` /
 * `duration_gdd`. The KC editor and clean client-parsed tables use a single
 * `duration` key. When both shapes coexist, every reader prefers the legacy
 * key — so an edited `duration` is persisted but never re-read.
 *
 * These helpers are CommonJS and dependency-free so they can be unit-tested
 * with `node --test` (see sciroot-webapp field __tests__).
 */

const GROWTH_PHASE_TYPES = ['byGrowthPhase', 'byGrowthPhaseGDD'];

/**
 * Resolve the authoritative duration for a phase, mirroring
 * GeneralClasses.PhaseRecord / the legacy Python PhaseRecord preference order
 * when only legacy keys are present — but preferring an explicit `duration`
 * when the editor (or a prior normalize) has set one.
 */
const resolvePhaseDuration = (phase, type) => {
  if (!phase || typeof phase !== 'object') {
    return undefined;
  }

  if ('duration' in phase) {
    return phase.duration;
  }

  if (type === 'byGrowthPhaseGDD' && 'duration_gdd' in phase) {
    return phase.duration_gdd;
  }

  if (type === 'byGrowthPhase' && 'duration_days' in phase) {
    return phase.duration_days;
  }

  return phase.duration;
};

/**
 * Collapse a raw crop-record / dual-key phase into a single-`duration` object.
 * Preserves name/kc/stress/is_dev as-is (unlike PhaseRecord, which renames
 * is_dev phases to "Development" and drops kc).
 */
const normalizeGrowthPhase = (phase, type) => {
  const normalized = Object.assign({}, phase);
  normalized.duration = resolvePhaseDuration(phase, type);
  delete normalized.duration_days;
  delete normalized.duration_gdd;
  return normalized;
};

/**
 * Build independent byGrowthPhase / byGrowthPhaseGDD arrays from raw crop
 * phenological_phases. Each array is a fresh map — no shared references.
 */
const buildNormalizedKcTableData = rawPhases => {
  const phases = Array.isArray(rawPhases) ? rawPhases : [];
  return {
    byGrowthPhase: phases.map(phase =>
      normalizeGrowthPhase(phase, 'byGrowthPhase')
    ),
    byGrowthPhaseGDD: phases.map(phase =>
      normalizeGrowthPhase(phase, 'byGrowthPhaseGDD')
    ),
  };
};

/**
 * Normalize an outgoing growth-phase array for create_field / update_field.
 * Ensures at most one duration key (`duration`) leaves the client.
 * Non-array payloads (e.g. byMonth objects) are returned unchanged.
 */
const normalizeKcGrowthPhasesForPayload = (data, type) => {
  if (!GROWTH_PHASE_TYPES.includes(type) || !Array.isArray(data)) {
    return data;
  }

  return data.map(phase => normalizeGrowthPhase(phase, type));
};

/**
 * Mirrors GeneralClasses.PhaseRecord's duration resolution (legacy keys win
 * when present). Used by regression tests to assert the reported bug: a
 * dual-key phase reopens with the stale legacy value, while a normalized
 * phase keeps the edited duration.
 */
const phaseRecordDuration = (phase, type) => {
  if (!phase || typeof phase !== 'object') {
    return undefined;
  }

  if (type === 'byGrowthPhaseGDD' && 'duration_gdd' in phase) {
    return phase.duration_gdd;
  }

  if (type === 'byGrowthPhase' && 'duration_days' in phase) {
    return phase.duration_days;
  }

  return phase.duration;
};

module.exports = {
  GROWTH_PHASE_TYPES,
  resolvePhaseDuration,
  normalizeGrowthPhase,
  buildNormalizedKcTableData,
  normalizeKcGrowthPhasesForPayload,
  phaseRecordDuration,
};
